import {
  AlertTriangle,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";
import type { BatchDetailResponse } from "../../gen/models";

interface BatchOutdatedBannerProps {
  staleReason?: string | null;
  actionLoading?: boolean;
  onReopen: () => void;
}

export function BatchOutdatedBanner({
  staleReason,
  actionLoading,
  onReopen,
}: BatchOutdatedBannerProps) {
  return (
    <div className="mx-5 mt-4 p-3.5 rounded-xl border border-rose-200 dark:border-rose-800/80 bg-rose-50/80 dark:bg-rose-950/40 text-xs text-rose-950 dark:text-rose-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
      <div className="flex items-start gap-2.5 min-w-0">
        <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300 flex items-center justify-center shrink-0 mt-0.5">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="space-y-1 min-w-0">
          <div className="font-bold text-rose-950 dark:text-rose-100 flex items-center gap-2">
            <span>Batch is Outdated / Stale</span>
            <span className="text-[10px] uppercase font-mono px-1.5 py-0.2 rounded bg-rose-200/60 dark:bg-rose-800/60 text-rose-800 dark:text-rose-200">
              Promotion Blocked
            </span>
          </div>
          <div className="text-rose-800/90 dark:text-rose-300/90 text-[11px] leading-relaxed">
            {staleReason || "One or more proposed changes has already been altered by another version in production."}
          </div>
          <div className="text-rose-700/80 dark:text-rose-400 text-[11px]">
            Approve &amp; Promote is blocked to prevent overwriting newer versions. Reset this batch to collecting mode to re-run processing with the current active versions.
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onReopen}
        disabled={actionLoading}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-2xs transition shrink-0 self-end sm:self-auto cursor-pointer"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        <span>Reset to Collecting</span>
      </button>
    </div>
  );
}

interface BatchCompletedBannerProps {
  batch: BatchDetailResponse;
}

export function BatchCompletedBanner({ batch }: BatchCompletedBannerProps) {
  const rulesCount = batch.draft_rules?.length || 0;
  const knowledgeCount = batch.draft_knowledge_items?.length || 0;
  const guardrailsCount = batch.draft_guardrails?.length || 0;

  return (
    <div className="mx-5 mt-4 p-3.5 rounded-xl border border-emerald-200 dark:border-emerald-800/80 bg-emerald-50/70 dark:bg-emerald-950/40 flex items-center gap-2.5 text-xs shrink-0">
      <div className="w-7 h-7 rounded-lg bg-emerald-100 dark:bg-emerald-900/60 text-emerald-700 dark:text-emerald-300 flex items-center justify-center shrink-0">
        <CheckCircle2 className="w-4 h-4" />
      </div>
      <div>
        <div className="font-bold text-emerald-950 dark:text-emerald-100 text-xs">
          Feedback Loop Successfully Completed &amp; Promoted
        </div>
        <div className="text-emerald-800/80 dark:text-emerald-300/80 text-[11px] mt-0.5">
          {rulesCount} rules, {knowledgeCount} knowledge items, and {guardrailsCount} guardrail rules were created and are currently active in production.
        </div>
      </div>
    </div>
  );
}

interface BatchFailedProcessingBannerProps {
  errorMessage?: string | null;
  runId?: string | null;
  actionLoading?: boolean;
  onReopen: () => void;
}

export function BatchFailedProcessingBanner({
  errorMessage,
  runId,
  actionLoading,
  onReopen,
}: BatchFailedProcessingBannerProps) {
  return (
    <div className="mx-5 mt-4 p-3.5 rounded-xl border border-rose-200 dark:border-rose-800/80 bg-rose-50/80 dark:bg-rose-950/40 text-xs text-rose-950 dark:text-rose-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shrink-0">
      <div className="flex items-start gap-2.5 min-w-0">
        <div className="w-7 h-7 rounded-lg bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300 flex items-center justify-center shrink-0 mt-0.5">
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="space-y-1 min-w-0">
          <div className="font-bold text-rose-950 dark:text-rose-100 flex items-center gap-2">
            <span>Batch Processing Failed</span>
            <span className="text-[10px] uppercase font-mono px-1.5 py-0.2 rounded bg-rose-200/60 dark:bg-rose-800/60 text-rose-800 dark:text-rose-200">
              Failed
            </span>
          </div>
          <div className="text-rose-800/90 dark:text-rose-300/90 text-[11px] leading-relaxed break-words font-mono">
            {errorMessage || "Synthesis workflow encountered an error or timeout during execution."}
          </div>
          <div className="text-rose-700/80 dark:text-rose-400 text-[11px] flex items-center gap-2">
            <span>Reset this batch to collecting mode to inspect comments, modify items, and try again.</span>
            {runId && (
              <a
                href={`/runs/${runId}`}
                className="underline hover:text-rose-900 dark:hover:text-rose-100 font-mono text-[10px]"
              >
                View Execution Trace &rarr;
              </a>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={onReopen}
        disabled={actionLoading}
        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold text-xs shadow-2xs transition shrink-0 self-end sm:self-auto cursor-pointer"
      >
        <RotateCcw className="w-3.5 h-3.5" />
        <span>Reset to Collecting</span>
      </button>
    </div>
  );
}

